import { useState } from 'react'

import { Comment01Icon, Flag01Icon, Share01Icon } from '@hugeicons/react'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { toast } from 'sonner'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { showErrorAlert } from '@/shared/lib/errors'
import { Card } from '@/shared/ui/Card'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { FeaturedListCardStrip, featuredListCardClassName } from '@/shared/ui/FeaturedListCardStrip'

import { CommentSection } from './CommentSection'
import { ReactionBar } from './ReactionBar'
import { useReportPost, useSharePost, useToggleReaction } from '../model/mutations'
import { usePostComments } from '../model/queries'

// Rodapé e menu: 44px de alvo de toque (meta de produto do ACCESSIBILITY.md,
// acima do piso de 24 da WCAG AA) sem inflar a altura visual do card.
const ACTION_CLASS =
  'flex min-h-9 items-center gap-1.5 rounded-lg px-2 text-sm text-gray-700 transition-colors hover:text-primary'

const AuthorAvatar = ({ user }) => {
  const photoUrl = user?.avatar?.file?.url
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className="h-9 w-9 shrink-0 rounded-full object-cover"
      />
    )
  }
  return (
    <div
      aria-hidden
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-base font-bold text-gray-700"
    >
      {user?.name?.[0]?.toUpperCase() || '?'}
    </div>
  )
}

export const timeAgo = dateStr => {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000
  if (diff < 60) return 'agora'
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`
  return `${Math.floor(diff / 86400)}d atrás`
}

// Denunciar era item único dentro de um menu "..." — bandeira direta é mais
// explícita que um kebab escondendo uma única ação.
const ReportButton = ({ onReport }) => (
  <button
    type="button"
    onClick={onReport}
    aria-label="Denunciar publicação"
    className="flex h-11 w-11 items-center justify-center rounded-lg text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
  >
    <Flag01Icon size={18} variant="stroke" />
  </button>
)

export const ParticipantPostHeader = ({ post, onReport }) => (
  <div className="flex items-center gap-2.5 px-4 pt-3">
    <AuthorAvatar user={post.user} />
    <div className="min-w-0 flex-1">
      <span className="block text-base font-bold leading-tight text-gray-900">
        {post.user?.name}
      </span>
      <span className="block text-2xs leading-tight text-gray-700">
        {timeAgo(post.published_at)}
      </span>
    </div>
    <ReportButton onReport={onReport} />
  </div>
)

const EventAvatar = ({ eventName, eventLogoUrl }) => {
  if (eventLogoUrl) {
    return (
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white p-1 ring-1 ring-gray-200">
        <img
          src={eventLogoUrl}
          alt=""
          className="h-full w-full rounded-full object-contain"
        />
      </div>
    )
  }

  return (
    <div
      aria-hidden
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-base font-bold text-gray-700"
    >
      {eventName?.[0]?.toUpperCase() || '?'}
    </div>
  )
}

export const ProducerPostHeader = ({ eventName, eventLogoUrl, publishedAt }) => (
  <div className="flex items-center gap-2.5 px-4 pt-3">
    <EventAvatar eventName={eventName} eventLogoUrl={eventLogoUrl} />
    <div className="min-w-0 flex-1">
      <span className="block truncate text-base font-bold leading-tight text-gray-900">
        {eventName}
      </span>
      <span className="block text-2xs leading-tight text-gray-700">
        {timeAgo(publishedAt)}
      </span>
    </div>
  </div>
)

export const PostCard = ({
  post,
  allowedEmojis = [],
  highlighted = false,
  initiallyExpandComments = false
}) => {
  const { eventSlug, digitalProduct } = useDigitalProductContext()
  const [showComments, setShowComments] = useState(initiallyExpandComments)
  const [confirmingReport, setConfirmingReport] = useState(false)
  const [reporting, setReporting] = useState(false)
  const { toggleReaction } = useToggleReaction(eventSlug, post.id)
  const { sharePost } = useSharePost(eventSlug, post.id)
  const { reportPost } = useReportPost(eventSlug, post.id)
  const { records: comments, isLoading: loadingComments } = usePostComments(
    eventSlug,
    showComments ? post.id : null
  )

  const isProducerPost = post.author_type === 'producer'
  const eventName = digitalProduct?.name || 'Evento'
  const eventLogoUrl = digitalProduct?.logo?.file?.url

  const sanitizedContent = DOMPurify.sanitize(post.content)

  const handleReport = async () => {
    setReporting(true)
    try {
      await reportPost()
      setConfirmingReport(false)
      toast.success('Denúncia enviada para a curadoria do evento.')
    } catch (err) {
      showErrorAlert(err)
    } finally {
      setReporting(false)
    }
  }

  const hasMedia = !!post.media_url
  const isVideo = post.post_type === 'video'
  const isAudio = post.post_type === 'audio'
  const isImage = hasMedia && !isVideo && !isAudio

  // Title is derived from content for participant posts (first line). Hide it
  // when it just repeats the start of the content to avoid duplication.
  const plainContent = sanitizedContent.replace(/<[^>]*>/g, '').trim()
  const titleText = post.title?.replace(/…$/, '').trim() ?? ''
  const showTitle = !!titleText && !plainContent.startsWith(titleText)

  return (
    <Card
      id={`post-${post.id}`}
      flush
      shadow={post.is_featured ? undefined : 'sm'}
      data-testid="feed-post"
      className={clsx(
        post.is_featured && featuredListCardClassName('mission'),
        post.is_sponsored && 'border-l-4 border-l-blue-400',
        highlighted && 'ring-2 ring-[var(--color-ring)] ring-offset-2 transition-shadow'
      )}
    >
      {post.is_featured && (
        <FeaturedListCardStrip label="Postagem em destaque" tone="mission" />
      )}
      {post.is_sponsored && (
        <div className="flex items-center justify-between border-b border-blue-100 bg-blue-50 px-4 py-1.5">
          <span className="text-2xs font-semibold text-blue-700">Patrocinado</span>
          {post.partner && (
            <span className="text-2xs text-blue-700">{post.partner.name}</span>
          )}
        </div>
      )}

      {isProducerPost ? (
        <ProducerPostHeader
          eventName={eventName}
          eventLogoUrl={eventLogoUrl}
          publishedAt={post.published_at}
        />
      ) : (
        <ParticipantPostHeader
          post={post}
          onReport={() => setConfirmingReport(true)}
        />
      )}

      {/* Content */}
      <div className={clsx('flex gap-3 px-4 pt-2', hasMedia && !isAudio && 'pb-0')}>
        <div className="min-w-0 flex-1">
          {showTitle && (
            <p className="mb-1 text-base font-semibold text-gray-900">{post.title}</p>
          )}
          <div
            className="prose prose-sm max-w-none whitespace-pre-line text-base text-gray-900"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        </div>
        {isVideo && (
          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-gray-100">
            <video
              src={post.media_url}
              className="h-full w-full object-cover"
              muted
              preload="metadata"
            />
          </div>
        )}
      </div>

      {isImage && (
        <div className="px-4 pt-2.5">
          <img
            src={post.media_url}
            alt=""
            className="max-h-80 w-full rounded-xl object-cover"
          />
        </div>
      )}

      {isAudio && (
        <div className="px-4 pt-2">
          <audio src={post.media_url} controls className="w-full" />
        </div>
      )}

      {/* Reactions + comments */}
      <div className="px-4 pb-2.5 pt-2">
        <ReactionBar
          allowedEmojis={allowedEmojis}
          reactionsSummary={post.reactions_summary}
          myReaction={post.my_reaction}
          onToggle={toggleReaction}
        />
        <div className="mt-1 flex items-center justify-between border-t border-border pt-1">
          <button
            type="button"
            onClick={() => setShowComments(!showComments)}
            aria-expanded={showComments}
            className={ACTION_CLASS}
          >
            <Comment01Icon size={16} variant="stroke" />
            {post.comments_count} comentário{post.comments_count !== 1 ? 's' : ''}
          </button>
          <button
            type="button"
            onClick={sharePost}
            aria-label="Compartilhar"
            className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-gray-700 transition-colors hover:text-primary"
          >
            <Share01Icon size={16} variant="stroke" />
          </button>
        </div>
      </div>

      {showComments && (
        <div className="border-t border-border px-4 pb-4">
          <CommentSection
            eventSlug={eventSlug}
            postId={post.id}
            comments={comments}
            isLoading={loadingComments}
          />
        </div>
      )}

      {confirmingReport && !isProducerPost && (
        <ConfirmDialog
          variant="destructive"
          title="Denunciar publicação"
          description="A publicação vai para a curadoria do evento, que decide se ela sai do feed. Você pode denunciar só uma vez."
          confirmLabel="Denunciar"
          confirmLoading={reporting}
          onConfirm={handleReport}
          onCancel={() => setConfirmingReport(false)}
        />
      )}
    </Card>
  )
}
